import { supabase } from '@/integrations/supabase/client';

// ─── Types ──────────────────────────────────────────────────

export interface GlossaryEntry {
  id: string;
  slug: string;
  term: string;
  definition: string;
  why_it_matters: string;
  day_to_day: string;
  what_to_do: string;
  when_to_get_help: string;
  related_terms: string[];
  related_checklist_slugs: string[];
  related_blog_slugs: string[];
  cta_type: string;
  seo_meta: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  category: string;
  search_volume_estimate: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChecklistItem {
  item: string;
  priority: string;
}

export interface ChecklistEntry {
  id: string;
  slug: string;
  title: string;
  situation: string;
  why_it_matters: string;
  safety_items: ChecklistItem[];
  wellness_items: ChecklistItem[];
  connection_items: ChecklistItem[];
  what_to_do_next: string;
  professional_resources: string | null;
  related_checklist_slugs: string[];
  related_glossary_slugs: string[];
  cta_type: string;
  seo_meta: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  category: string;
  search_volume_estimate: number;
  printable: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Glossary Service ───────────────────────────────────────

export async function fetchGlossaryEntry(slug: string): Promise<GlossaryEntry | null> {
  const { data, error } = await supabase
    .from('pseo_glossary')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .single();

  if (error || !data) return null;
  return data as unknown as GlossaryEntry;
}

export async function fetchGlossaryList(): Promise<GlossaryEntry[]> {
  const { data, error } = await supabase
    .from('pseo_glossary')
    .select('id, slug, term, definition, category, search_volume_estimate, seo_meta, created_at')
    .eq('status', 'published')
    .order('term', { ascending: true });

  if (error || !data) return [];
  return data as unknown as GlossaryEntry[];
}

export async function fetchGlossarySlugs(): Promise<string[]> {
  const { data, error } = await supabase
    .from('pseo_glossary')
    .select('slug')
    .eq('status', 'published');

  if (error || !data) return [];
  return data.map((d: any) => d.slug);
}

// ─── Checklists Service ─────────────────────────────────────

export async function fetchChecklistEntry(slug: string): Promise<ChecklistEntry | null> {
  const { data, error } = await supabase
    .from('pseo_checklists')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .single();

  if (error || !data) return null;
  return data as unknown as ChecklistEntry;
}

export async function fetchChecklistList(): Promise<ChecklistEntry[]> {
  const { data, error } = await supabase
    .from('pseo_checklists')
    .select('id, slug, title, situation, category, search_volume_estimate, seo_meta, printable, created_at')
    .eq('status', 'published')
    .order('title', { ascending: true });

  if (error || !data) return [];
  return data as unknown as ChecklistEntry[];
}

export async function fetchChecklistSlugs(): Promise<string[]> {
  const { data, error } = await supabase
    .from('pseo_checklists')
    .select('slug')
    .eq('status', 'published');

  if (error || !data) return [];
  return data.map((d: any) => d.slug);
}

// ─── States Service ─────────────────────────────────────────

export interface StateCitation {
  section: string;
  url: string;
  verified_at: string;
}

export interface StateEntry {
  id: string;
  slug: string;
  state_code: string;
  state_name: string;
  region: 'Northeast' | 'Midwest' | 'South' | 'West';
  overview: string | null;
  demographics: {
    senior_pct?: number;
    senior_count?: number;
    median_age?: number;
    source_url?: string;
  };
  hcbs_waiver: {
    name?: string;
    description?: string;
    coverage_summary?: string;
    eligibility_url?: string;
    application_url?: string;
    covers_home_mods?: boolean;
    source_url?: string;
  };
  area_agencies: {
    count?: number;
    directory_url?: string;
    notable_orgs?: Array<{ name: string; url: string }>;
  };
  tax_programs: {
    programs?: Array<{ name: string; description: string; url: string }>;
  };
  fall_prevention: {
    program_name?: string;
    agency?: string;
    url?: string;
    description?: string;
  };
  pace_programs: {
    available?: boolean;
    find_url?: string;
    notes?: string;
  };
  ot_licensing: {
    board_name?: string;
    board_url?: string;
  };
  citations: StateCitation[];
  related_state_slugs: string[];
  related_blog_slugs: string[];
  seo_meta: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  cta_type: string;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchStateEntry(slug: string): Promise<StateEntry | null> {
  const { data, error } = await supabase
    .from('pseo_states')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .single();

  if (error || !data) return null;
  return data as unknown as StateEntry;
}

export async function fetchStateList(): Promise<StateEntry[]> {
  const { data, error } = await supabase
    .from('pseo_states')
    .select('id, slug, state_code, state_name, region, seo_meta, updated_at')
    .eq('status', 'published')
    .order('state_name', { ascending: true });

  if (error || !data) return [];
  return data as unknown as StateEntry[];
}

export async function fetchStateSlugs(): Promise<string[]> {
  const { data, error } = await supabase
    .from('pseo_states')
    .select('slug')
    .eq('status', 'published');

  if (error || !data) return [];
  return data.map((d: any) => d.slug);
}
